import React, { useEffect, useState } from 'react';
import { Link as ScrollLink } from 'react-scroll'; // Importing from react-scroll
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  Box,
  Container,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

const NAV_ITEMS = [
  { label: 'Home', target: 'hero' },
  { label: 'Features', target: 'features' },
  { label: 'About Us', target: 'about' },
  { label: 'Contact', target: 'contact' },
];

const Navbar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        color: 'text.primary',
        backdropFilter: 'blur(14px)',
        backgroundColor: scrolled ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.6)',
        borderBottom: scrolled ? '1px solid rgba(17,24,39,0.08)' : '1px solid transparent',
        boxShadow: scrolled ? '0 8px 30px rgba(17,24,39,0.06)' : 'none',
        transition: 'all 0.3s ease',
      }}
    >
      <Container maxWidth="lg" disableGutters>
        <Toolbar sx={{ px: { xs: 2, md: 1 }, minHeight: { xs: 68, md: 76 } }}>
          {/* Logo section */}
          <Box
            component={ScrollLink}
            to="hero"
            smooth
            duration={500}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer', flexGrow: 1 }}
          >
            <Box
              component="img"
              src="/app_icon.png"
              alt="Cizlot logo"
              sx={{ width: 38, height: 38, borderRadius: '12px' }}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.02em',
                background: 'linear-gradient(90deg, #2085D8, #0EA5E9)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              Cizlot
            </Typography>
          </Box>

          {/* Desktop nav buttons */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
            {NAV_ITEMS.map((item) => (
              <Button
                key={item.target}
                component={ScrollLink}
                to={item.target}
                smooth
                duration={500}
                offset={-80}
                spy
                activeClass="nav-active"
                color="inherit"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  '&:hover': { color: 'primary.main', backgroundColor: 'rgba(32,133,216,0.06)' },
                  '&.nav-active': { color: 'primary.main', backgroundColor: 'rgba(32,133,216,0.08)' },
                }}
              >
                {item.label}
              </Button>
            ))}
            <Button
              component={RouterLink}
              to="/pricing"
              color="inherit"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                '&:hover': { color: 'primary.main', backgroundColor: 'rgba(32,133,216,0.06)' },
              }}
            >
              Pricing
            </Button>
            <Button
              variant="outlined"
              href="https://partner.cizlot.com/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                ml: 1,
                borderColor: 'rgba(17,24,39,0.15)',
                color: 'text.primary',
                '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
              }}
            >
              Sign In Cizlot Partner
            </Button>
            <Button
              variant="contained"
              color="primary"
              href="https://play.google.com/store/apps/details?id=com.cizlot.cizlot_user_app"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ ml: 1 }}
            >
              Get the App
            </Button>
          </Box>

          {/* Hamburger menu for mobile */}
          <IconButton
            edge="end"
            aria-label="menu"
            onClick={toggleDrawer}
            sx={{ display: { xs: 'inline-flex', md: 'none' }, color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>

        {/* Mobile quick actions: always visible, not hidden inside the hamburger menu */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.75,
            px: 2,
            pb: 1.25,
          }}
        >
          <Button
            component={RouterLink}
            to="/pricing"
            variant="outlined"
            size="small"
            sx={{ fontSize: '0.8rem', paddingInline: '0.75rem', whiteSpace: 'nowrap' }}
          >
            Pricing
          </Button>
          <Button
            variant="outlined"
            size="small"
            href="https://partner.cizlot.com/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: '0.8rem', paddingInline: '0.75rem', whiteSpace: 'nowrap' }}
          >
            Partner Sign In
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            href="https://play.google.com/store/apps/details?id=com.cizlot.cizlot_user_app"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: '0.8rem', paddingInline: '0.85rem', whiteSpace: 'nowrap' }}
          >
            Get the App
          </Button>
        </Box>
      </Container>

      {/* Drawer for mobile */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer}
        PaperProps={{ sx: { width: 280, p: 2, borderTopLeftRadius: 20, borderBottomLeftRadius: 20 } }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ fontWeight: 800 }} className="gradient-text">
            Cizlot
          </Typography>
          <IconButton onClick={toggleDrawer} aria-label="close menu">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box display="flex" flexDirection="column" gap={0.5}>
          {NAV_ITEMS.map((item) => (
            <Button
              key={item.target}
              component={ScrollLink}
              to={item.target}
              smooth
              duration={500}
              offset={-70}
              onClick={toggleDrawer}
              fullWidth
              sx={{
                justifyContent: 'flex-start',
                borderRadius: 2,
                py: 1.2,
                color: 'text.primary',
                fontWeight: 500,
              }}
            >
              {item.label}
            </Button>
          ))}
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Navbar;
